import type { AgentToolResult } from "@earendil-works/pi-agent-core";
import { Box, type Component, Container, getCapabilities, Image, Spacer, Text, type TUI } from "@earendil-works/pi-tui";
import type { TSchema } from "typebox";
import type { ToolDefinition, ToolRenderContext, ToolRenderers } from "../../../core/extensions/types.ts";
import { createAllToolDefinitions, type ToolName } from "../../../core/tools/index.js";
import { formatToolCallWithArgs, getTextOutput as getRenderedTextOutput } from "../../../core/tools/render-utils.ts";
import { ensurePngTranscoder } from "../../../utils/image-convert.ts";
import { theme } from "../theme/theme.js";
import { parenthesizedKeyHint } from "./keybinding-hints.js";

/** Extension tools without a renderer show this many output lines before collapsing. */
const FALLBACK_PREVIEW_LINES = 10;

export interface ToolExecutionOptions {
	showImages?: boolean;
	imageWidthCells?: number;
	outputPad?: number;
}

type RenderableToolResult = Omit<AgentToolResult<unknown>, "details"> & {
	details?: unknown;
	isError: boolean;
	durationMs?: number;
};
type RenderableImageContent = Extract<RenderableToolResult["content"][number], { type: "image" }>;
type DisposableRendererComponent = Component & { dispose?: () => void };
/** Keep isolated-engine image components stable so pi-tui can reuse Kitty uploads. */
interface RenderedImageSpec {
	data: string;
	mimeType: string;
	protocol: "kitty" | "iterm2";
	widthCells: number;
}
const SUBAGENT_RESULT_ANIMATION_TIMER_KEY = "subagentResultAnimationTimer";
const SUBAGENT_RESULT_ANIMATION_CLEANUP_KEY = "subagentResultAnimationCleanup";

export class ToolExecutionComponent extends Container {
	private contentBox: Box;
	private contentText: Text;
	private selfRenderContainer: Container;
	private callRendererComponent?: Component;
	private resultRendererComponent?: Component;
	private rendererState: Record<string, unknown> = {};
	private imageComponents: Image[] = [];
	private imageSpacers: Spacer[] = [];
	private toolName: string;
	private toolCallId: string;
	private args: unknown;
	private expanded = false;
	private showImages: boolean;
	private imageWidthCells: number;
	private outputPad: number;
	private isPartial = true;
	private toolDefinition?: ToolRenderers;
	private builtInToolDefinition?: ToolDefinition<TSchema, unknown>;
	private ui: TUI;
	private cwd: string;
	private executionStarted = false;
	private argsComplete = false;
	private result?: RenderableToolResult;
	private renderedImageSpecs: RenderedImageSpec[] = [];
	private hideComponent = false;

	constructor(
		toolName: string,
		toolCallId: string,
		args: unknown,
		options: ToolExecutionOptions = {},
		toolDefinition: ToolRenderers | undefined,
		ui: TUI,
		cwd: string,
	) {
		super();
		this.toolName = toolName;
		this.toolCallId = toolCallId;
		this.args = args;
		this.toolDefinition = toolDefinition;
		this.builtInToolDefinition = createAllToolDefinitions(cwd)[toolName as ToolName];
		this.showImages = options.showImages ?? true;
		this.imageWidthCells = options.imageWidthCells ?? 60;
		this.outputPad = options.outputPad ?? 1;
		this.ui = ui;
		this.cwd = cwd;

		this.addChild(new Spacer(1));

		// Always create all shell variants. contentBox is used for default renderer-based composition.
		// selfRenderContainer is used when the tool renders its own framing.
		// contentText is reserved for generic fallback rendering when no tool definition exists.
		this.contentBox = new Box(1, 1, (text: string) => theme.bg("toolPendingBg", text));
		this.contentText = new Text("", 1, 1, (text: string) => theme.bg("toolPendingBg", text));
		this.selfRenderContainer = new Container();

		if (this.hasRendererDefinition()) {
			this.addChild(this.getRenderShell() === "self" ? this.selfRenderContainer : this.contentBox);
		} else {
			this.addChild(this.contentText);
		}

		this.updateDisplay();
	}

	private getCallRenderer(): ToolDefinition<TSchema, unknown>["renderCall"] | undefined {
		if (!this.builtInToolDefinition) {
			return this.toolDefinition?.renderCall;
		}
		if (!this.toolDefinition) {
			return this.builtInToolDefinition.renderCall;
		}
		return this.toolDefinition.renderCall ?? this.builtInToolDefinition.renderCall;
	}

	private getResultRenderer(): ToolDefinition<TSchema, unknown>["renderResult"] | undefined {
		if (!this.builtInToolDefinition) {
			return this.toolDefinition?.renderResult;
		}
		if (!this.toolDefinition) {
			return this.builtInToolDefinition.renderResult;
		}
		return this.toolDefinition.renderResult ?? this.builtInToolDefinition.renderResult;
	}

	private hasRendererDefinition(): boolean {
		return this.builtInToolDefinition !== undefined || this.toolDefinition !== undefined;
	}

	private getRenderShell(): "default" | "self" {
		if (!this.builtInToolDefinition) {
			return this.toolDefinition?.renderShell ?? "default";
		}
		if (!this.toolDefinition) {
			return this.builtInToolDefinition.renderShell ?? "default";
		}
		return this.toolDefinition.renderShell ?? this.builtInToolDefinition.renderShell ?? "default";
	}

	private getRenderContext(lastComponent: Component | undefined): ToolRenderContext {
		return {
			args: this.args,
			toolCallId: this.toolCallId,
			invalidate: () => {
				this.invalidate();
				this.ui.requestRender();
			},
			lastComponent,
			state: this.rendererState,
			cwd: this.cwd,
			executionStarted: this.executionStarted,
			argsComplete: this.argsComplete,
			isPartial: this.isPartial,
			expanded: this.expanded,
			showImages: this.showImages,
			isError: this.result?.isError ?? false,
			durationMs: this.isPartial ? undefined : this.result?.durationMs,
			outputPad: this.outputPad,
		};
	}

	private createCallFallback(): Component {
		return new Text(formatToolCallWithArgs(this.toolName, this.args, theme, this.expanded), 0, 0);
	}

	private createResultFallback(): Component | undefined {
		const output = this.getTextOutput();
		if (!output) {
			return undefined;
		}

		const lines = output.split("\n");
		const displayLines = this.expanded ? lines : lines.slice(0, FALLBACK_PREVIEW_LINES);
		const remaining = lines.length - displayLines.length;
		let text = displayLines.map((line) => theme.fg("toolOutput", line)).join("\n");
		if (remaining > 0) {
			text +=
				theme.fg("muted", "\n... ") + parenthesizedKeyHint("app.tools.expand", "Expand", `${remaining} more lines`);
		}
		return new Text(text, 0, 0);
	}

	updateArgs(args: unknown): void {
		this.args = args;
		this.updateDisplay();
	}

	// markExecutionStarted / setArgsComplete are idempotent: re-seeding an
	// already-started component must not schedule another render, or the
	// remote-renderer bridge (EngineRenderService) turns every repeated
	// engine_tool_render into a fresh invalidate → render ping-pong (TUI
	// flicker while a workflow stage streams).
	markExecutionStarted(): void {
		if (this.executionStarted) return;
		this.executionStarted = true;
		this.updateDisplay();
		this.ui.requestRender();
	}

	setArgsComplete(): void {
		if (this.argsComplete) return;
		this.argsComplete = true;
		this.updateDisplay();
		this.ui.requestRender();
	}

	updateResult(result: RenderableToolResult, isPartial = false): void {
		this.result = result;
		this.isPartial = isPartial;
		this.updateDisplay();
	}

	setExpanded(expanded: boolean): void {
		if (this.expanded === expanded) return;
		this.expanded = expanded;
		this.updateDisplay();
	}

	setOutputPad(outputPad: number): void {
		this.outputPad = outputPad;
		this.updateDisplay();
	}

	setShowImages(show: boolean): void {
		if (this.showImages === show) return;
		this.showImages = show;
		this.updateDisplay();
	}

	setImageWidthCells(width: number): void {
		const nextWidth = Math.max(1, Math.floor(width));
		if (this.imageWidthCells === nextWidth) return;
		this.imageWidthCells = nextWidth;
		this.updateDisplay();
	}

	override invalidate(): void {
		super.invalidate();
		this.updateDisplay();
	}

	dispose(): void {
		this.disposeRendererComponent(this.callRendererComponent);
		this.disposeRendererComponent(this.resultRendererComponent);
		this.callRendererComponent = undefined;
		this.resultRendererComponent = undefined;
		this.clearRendererStateTimer(SUBAGENT_RESULT_ANIMATION_TIMER_KEY, SUBAGENT_RESULT_ANIMATION_CLEANUP_KEY);
		this.clearRendererStateTimer("interval");
	}

	override render(width: number): string[] {
		if (this.hideComponent) {
			return [];
		}

		if (this.hasRendererDefinition() && this.getRenderShell() === "self") {
			const contentLines = this.selfRenderContainer.render(width);
			if (contentLines.length === 0 && this.imageComponents.length === 0) {
				return [];
			}

			const lines: string[] = [];
			if (contentLines.length > 0) {
				lines.push("");
				lines.push(...contentLines);
			}
			for (let i = 0; i < this.imageComponents.length; i++) {
				const spacer = this.imageSpacers[i];
				if (spacer) {
					lines.push(...spacer.render(width));
				}
				const imageComponent = this.imageComponents[i];
				if (imageComponent) {
					lines.push(...imageComponent.render(width));
				}
			}
			return lines;
		}

		return super.render(width);
	}

	private updateDisplay(): void {
		const bgFn = this.isPartial
			? (text: string) => theme.bg("toolPendingBg", text)
			: this.result?.isError
				? (text: string) => theme.bg("toolErrorBg", text)
				: (text: string) => theme.bg("toolSuccessBg", text);

		let hasContent = false;
		this.hideComponent = false;
		if (this.hasRendererDefinition()) {
			const renderContainer = this.getRenderShell() === "self" ? this.selfRenderContainer : this.contentBox;
			if (renderContainer instanceof Box) {
				renderContainer.setBgFn(bgFn);
				renderContainer.setPaddingX(this.outputPad);
			}
			renderContainer.clear();

			const callRenderer = this.getCallRenderer();
			if (!callRenderer) {
				renderContainer.addChild(this.createCallFallback());
				hasContent = true;
			} else {
				try {
					const component = callRenderer(this.args, theme, this.getRenderContext(this.callRendererComponent));
					this.callRendererComponent = component;
					renderContainer.addChild(component);
					hasContent = true;
				} catch {
					this.callRendererComponent = undefined;
					// A custom renderer may intentionally omit private arguments.
					// Do not disclose them if that renderer fails.
					renderContainer.addChild(new Text(theme.fg("toolTitle", theme.bold(this.toolName)), 0, 0));
					hasContent = true;
				}
			}

			if (this.result) {
				const resultRenderer = this.getResultRenderer();
				if (!resultRenderer) {
					const component = this.createResultFallback();
					if (component) {
						renderContainer.addChild(component);
						hasContent = true;
					}
				} else {
					try {
						const component = resultRenderer(
							{ content: this.result.content, details: this.result.details, terminate: this.result.terminate },
							{ expanded: this.expanded, isPartial: this.isPartial },
							theme,
							this.getRenderContext(this.resultRendererComponent),
						);
						this.resultRendererComponent = component;
						renderContainer.addChild(component);
						hasContent = true;
					} catch {
						this.resultRendererComponent = undefined;
						const component = this.createResultFallback();
						if (component) {
							renderContainer.addChild(component);
							hasContent = true;
						}
					}
				}
			}
		} else {
			this.contentText.setCustomBgFn(bgFn);
			this.contentText.setPaddingX(this.outputPad);
			this.contentText.setText(this.formatToolExecution());
			hasContent = true;
		}

		this.syncImageComponents();

		if (this.hasRendererDefinition() && !hasContent && this.imageComponents.length === 0) {
			this.hideComponent = true;
		}
	}

	private syncImageComponents(): void {
		const capabilities = getCapabilities();
		const nextSpecs: RenderedImageSpec[] = [];
		if (this.result && this.showImages && capabilities.images) {
			const imageBlocks = this.result.content.filter((c): c is RenderableImageContent => c.type === "image");
			for (const image of imageBlocks) {
				if (!image.data || !image.mimeType) continue;
				nextSpecs.push({
					data: image.data,
					mimeType: image.mimeType,
					protocol: capabilities.images,
					widthCells: this.imageWidthCells,
				});
				if (image.mimeType !== "image/png") {
					ensurePngTranscoder(() => {
						this.invalidate();
						this.ui.requestRender();
					});
				}
			}
		}

		const unchanged =
			nextSpecs.length === this.renderedImageSpecs.length &&
			nextSpecs.every((spec, index) => {
				const previous = this.renderedImageSpecs[index];
				return (
					previous?.data === spec.data &&
					previous.mimeType === spec.mimeType &&
					previous.protocol === spec.protocol &&
					previous.widthCells === spec.widthCells
				);
			});
		if (unchanged) return;

		for (const image of this.imageComponents) this.removeChild(image);
		this.imageComponents = [];
		for (const spacer of this.imageSpacers) this.removeChild(spacer);
		this.imageSpacers = [];
		this.renderedImageSpecs = nextSpecs;

		for (const spec of nextSpecs) {
			const spacer = new Spacer(1);
			this.addChild(spacer);
			this.imageSpacers.push(spacer);
			const image = new Image(
				spec.data,
				spec.mimeType,
				{ fallbackColor: (s: string) => theme.fg("toolOutput", s) },
				{ maxWidthCells: spec.widthCells },
			);
			this.imageComponents.push(image);
			this.addChild(image);
		}
	}
	private getTextOutput(): string {
		return getRenderedTextOutput(this.result, this.showImages);
	}

	private disposeRendererComponent(component: Component | undefined): void {
		(component as DisposableRendererComponent | undefined)?.dispose?.();
	}

	private clearRendererStateTimer(timerKey: string, cleanupKey?: string): void {
		const cleanup = cleanupKey ? this.rendererState[cleanupKey] : undefined;
		if (typeof cleanup === "function") {
			cleanup();
			return;
		}
		const timer = this.rendererState[timerKey];
		if (timer === undefined) return;
		clearInterval(timer as ReturnType<typeof setInterval>);
		this.rendererState[timerKey] = undefined;
		if (cleanupKey) this.rendererState[cleanupKey] = undefined;
	}

	private formatToolExecution(): string {
		let text = theme.fg("toolTitle", theme.bold(this.toolName));
		const content = JSON.stringify(this.args, null, 2);
		if (content) {
			text += `\n\n${content}`;
		}
		const output = this.getTextOutput();
		if (output) {
			text += `\n${output}`;
		}
		return text;
	}
}
